/**
 * „Dodaj do Kalendarza Apple": the feed's read, the press that opens Calendar's own subscribe
 * dialog (enabling the feed first when it is off), and „Wyłącz link" / „Utwórz nowy", each asked
 * first. What stands is `model/appleCalendar`'s; the address is never drawn (calendar-sync R10).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  deleteMeCalendarFeed,
  postMeCalendarFeed,
  postMeCalendarFeedRotate,
} from '~/shared/api/generated/endpoints/my-calendar-feed/my-calendar-feed';
import type { TrainWithMeModulesTrainingDtosResponsesCalendarFeedResponse as CalendarFeed } from '~/shared/api/generated/model';
import { ApiError } from '~/shared/api/mutator';
import { isApplePlatform } from '~/shared/lib/applePlatform';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';
import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';

import {
  calendarFeedQueryKey,
  calendarFeedQueryOptions,
  isWebcalUrl,
  openInCalendar,
  subscribeTarget,
} from '~/entities/calendar-feed';

import { appleCalendarView, type AppleCalendarView } from '../model/appleCalendar';

const KEY = 'web:account.calendar.apple';

type Asked = 'disable' | 'rotate';

export interface AppleCalendarApi {
  readonly view: AppleCalendarView;
  /**
   * Whether `view` is the answer rather than the wait: the read is in (or failed), or there is no
   * read to make on this device. The Integracje tab draws its rows once Google's, this and the
   * standing link's are all settled, so a row arriving late does not push the rows under it.
   */
  readonly settled: boolean;
  /** When the read last answered — the clock „pobrał treningi" is worded against. */
  readonly answeredAt: number;
  readonly busy: boolean;
  readonly refusal: string | null;
  readonly prompt: ConfirmPrompt | null;
  readonly announcement: string;
  readonly onSubscribe: () => void;
  readonly onAsk: (act: Asked) => void;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/** @param active whether the Integracje tab is on screen. */
export function useAppleCalendar(active: boolean): AppleCalendarApi {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { message: announcement, announce } = useAnnouncer();
  const apple = isApplePlatform(globalThis.navigator);
  const read = useQuery(calendarFeedQueryOptions(active && apple));
  const [asked, setAsked] = useState<Asked | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const refused = (error: unknown) => {
    setAsked(null);
    const unavailable = error instanceof ApiError && error.code === 'calendar_feed_unavailable';
    setRefusal(t(unavailable ? `${KEY}.unavailableNow` : `${KEY}.failed`));
    void queryClient.invalidateQueries({ queryKey: calendarFeedQueryKey });
  };
  const landed = (answer: CalendarFeed) => {
    queryClient.setQueryData(calendarFeedQueryKey, answer);
    setRefusal(null);
  };

  const enable = useMutation({
    mutationFn: () => postMeCalendarFeed(),
    onSuccess: (answer) => {
      landed(answer);
      if (isWebcalUrl(answer.webcalUrl)) openInCalendar(answer.webcalUrl);
    },
    onError: refused,
  });
  const rotate = useMutation({
    mutationFn: () => postMeCalendarFeedRotate(),
    onSuccess: (answer) => {
      landed(answer);
      setAsked(null);
      announce(t(`${KEY}.done.rotated`));
    },
    onError: refused,
  });
  const disable = useMutation({
    mutationFn: () => deleteMeCalendarFeed(),
    onSuccess: () => {
      queryClient.setQueryData<CalendarFeed>(calendarFeedQueryKey, (old) =>
        old === undefined ? old : { ...old, enabled: false, url: null, webcalUrl: null, lastFetchedAt: null },
      );
      setAsked(null);
      setRefusal(null);
      announce(t(`${KEY}.done.disabled`));
    },
    onError: refused,
  });
  const busy = enable.isPending || rotate.isPending || disable.isPending;

  return {
    view: appleCalendarView(read.data, apple),
    settled: !apple || !read.isPending,
    answeredAt: read.dataUpdatedAt,
    busy,
    refusal,
    prompt:
      asked === null
        ? null
        : {
            title: t(`${KEY}.${asked}Title`),
            description: t(`${KEY}.${asked}Body`),
            cancelLabel: t('common.cancel'),
            confirmLabel: t(`${KEY}.${asked}Confirm`),
            destructiveConfirm: asked === 'disable',
            busy,
          },
    announcement,
    onSubscribe: () => {
      if (busy) return;
      setRefusal(null);
      const target = subscribeTarget(read.data);
      if (target !== null) openInCalendar(target);
      else enable.mutate();
    },
    onAsk: (act) => {
      if (busy) return;
      setRefusal(null);
      setAsked(act);
    },
    onConfirm: () => {
      if (busy || asked === null) return;
      if (asked === 'disable') disable.mutate();
      else rotate.mutate();
    },
    onCancel: () => {
      if (!busy) setAsked(null);
    },
  };
}
